import { useEffect, useMemo } from "react";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { fetchVariantsByIds } from "lib/core/apiMethods";
import { DiscountAchieverVariant } from "lib/types/discountAchiever";
import { getDefaultProductImage, getVariantImage } from "lib/utils/variantImage";

/**
 * Sibling variants of everything in the cart, keyed by product_id, fetched once when the
 * cart/checkout opens so each ItemCard can render its picker without a per-card request.
 *
 * One variant id per cart line is enough — the lookup inlines the parent product, which carries
 * the product's full variant list. Consumers read the map off CheckoutProvider state.
 */
export const useCartItemVariants = () => {
  const {
    state: { checkoutItems, itemVariants },
    actions: { setItemVariants },
  } = useCheckoutContext();
  const {
    state: { merchant },
  } = useMerchantContext();

  // Refetch when the set of products OR the representative variant per product changes (e.g. a
  // variant swap) — but not on every quantity edit, since that doesn't affect sibling data.
  const { productIdsKey, variantIds } = useMemo(() => {
    const seenProducts = new Set<string>();
    const ids: string[] = [];
    const keyParts: string[] = [];
    (checkoutItems ?? []).forEach((item) => {
      if (!item?.product_id || !item?.variant_id || seenProducts.has(item.product_id)) return;
      seenProducts.add(item.product_id);
      ids.push(item.variant_id);
      keyParts.push(`${item.product_id}:${item.variant_id}`);
    });
    return { productIdsKey: keyParts.sort().join(","), variantIds: ids };
  }, [checkoutItems]);

  useEffect(() => {
    if (!Boolean(productIdsKey) || !Boolean(merchant?.merchantId)) return;

    let isStale = false;

    const loadVariants = async () => {
      try {
        const entries = await fetchVariantsByIds(merchant?.merchantId ?? "", variantIds);
        if (isStale) return;

        const variantsByProduct = entries.reduce<Record<string, DiscountAchieverVariant[]>>((acc, entry) => {
          const productId = entry?.product?.product_id;
          if (!productId || acc[productId]) return acc;

          const images = entry?.product?.images ?? [];
          const defaultImage = getDefaultProductImage(images);

          acc[productId] = (entry?.product?.variants ?? []).map((variant) => ({
            ...variant,
            imageUrl: getVariantImage(images, variant.variant_id, defaultImage),
          }));
          return acc;
        }, {});

        setItemVariants(variantsByProduct);
      } catch (error) {
        // A failed lookup only means no picker — the cart itself must still render.
        console.error(error);
      }
    };

    loadVariants();

    return () => {
      isStale = true;
    };
    // checkoutItems/variantIds are intentionally excluded — productIdsKey is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdsKey, merchant?.merchantId]);

  return { itemVariants };
};
